/* eslint-disable no-undef, no-unused-vars */
import { loadStripe } from '@stripe/stripe-js'
import { SnackbarProgrammatic as Snackbar } from 'buefy'

export default {
    namespaced: true,
    state: () => ({
        stripe: null,
        stripeLoading: null,
        // Fixed-price bundles from the Stripe Dashboard (GET /api/checkout/prices)
        // — the only things a user can ever buy. No custom amounts.
        bundles: [],
        bundlesLoading: false,
        clientSecret: null,
    }),
    getters: {
        getBundles: state => type => state.bundles.filter(bundle => bundle.type === type),
    },
    mutations: {
        setStripe(state, stripe) { state.stripe = stripe },
        setStripeLoading(state, promise) { state.stripeLoading = promise },
        setBundles(state, bundles) { state.bundles = bundles },
        setBundlesLoading(state, loading) { state.bundlesLoading = loading },
        setClientSecret(state, clientSecret) { state.clientSecret = clientSecret },
    },
    actions: {
        // Loads Stripe.js once (it injects its own <script> tag) and caches
        // the instance/promise so repeat calls are free.
        init({ state, commit }) {
            if (state.stripe || state.stripeLoading) return state.stripeLoading
            const promise = loadStripe(process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY)
                .then(stripe => {
                    commit('setStripe', stripe)
                    return stripe
                })
            commit('setStripeLoading', promise)
            return promise
        },

        async fetchBundles({ commit }) {
            commit('setBundlesLoading', true)
            try {
                const res = await fetch('/api/checkout/prices')
                const json = await res.json()
                commit('setBundles', json.bundles || [])
            } catch (err) {
                console.error(err)
                Snackbar.open({
                    message: 'Could not load purchase options. Try again in a moment.',
                    type: 'is-danger'
                })
            } finally {
                commit('setBundlesLoading', false)
            }
        },

        // Starts a checkout for one fixed bundle — the server looks up the
        // amount from the Price itself, this never sends an amount.
        async startCheckout({ rootGetters, commit }, bundle) {
            if (!rootGetters.getUser || !rootGetters.getToken) return false

            const gtag = this._vm.$gtag
            gtag.event('begin_checkout', {
                event_category: 'ecommerce',
                event_label: bundle.id,
                value: bundle.quantity
            })

            const res = await fetch('/api/checkout/createPaymentIntent', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${rootGetters.getToken}`
                },
                body: JSON.stringify({
                    customerID: rootGetters.getUser.id,
                    priceId: bundle.id,
                })
            })
            const json = await res.json()

            if (json.error) {
                Snackbar.open({ message: json.error, type: 'is-danger' })
                if (json.redirect) window.location = json.redirect
                return false
            }

            commit('setClientSecret', json.clientSecret)
            return json.clientSecret
        },

        resetCheckout({ commit }) {
            commit('setClientSecret', null)
        },
    }
}
